import * as React from "react";
import { useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Box, Paper, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useBrands } from "./utils/useBrands";
import { countByFlag } from "./utils/helpersOfBrands";
import AllBrands from "./AllBrands/AllBrands";
import NeedsConfiguration from "./NeedsConfiguration/NeedsConfiguration";
import BrandsSkeleton from "./BrandsSkeleton";

const BRANDS_TAB_KEYS = ["all_brands", "needs_configuration"];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `brands-tab-${index}`,
    "aria-controls": `brands-tabpanel-${index}`,
  };
}

function TabLabelWithCount({ label, count }: { label: string; count: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <span>{label}</span>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 20,
          height: 22,
          px: 0.5,
          borderRadius: "9px",
          border: "1px solid",
          borderColor: "#D32F2F80",
          backgroundColor: "#FDEDED",
          color: "#5F2120",
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: 1,
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

const BrandsTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const initialTab = tabFromUrl
    ? Math.max(BRANDS_TAB_KEYS.indexOf(tabFromUrl), 0)
    : 0;
  const [value, setValue] = React.useState(initialTab);

  const { fetchedBrandsData, allBrandsData, isFetchedBrandsData } = useBrands();

  useEffect(() => {
    fetchedBrandsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsConfigurationCount = countByFlag(
    allBrandsData,
    "is_needs_configuration",
  );

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", BRANDS_TAB_KEYS[newValue]);
      return next;
    });
  };

  return (
    <Paper sx={{ width: "100%", p: 2, backgroundColor: "#fff" }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Brands
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Get, edit and update brand data across Unifier, Fetcher and Business
        Advisor, and track which brands still need configuring.
      </Typography>

      <Tabs
        value={value}
        onChange={handleChange}
        textColor="secondary"
        indicatorColor="secondary"
        aria-label="Brands tabs"
      >
        <Tab label="All Brands" data-tracker="1070" {...a11yProps(0)} />
        <Tab
          label={
            <TabLabelWithCount
              label="Needs Configuration"
              count={needsConfigurationCount}
            />
          }
          data-tracker="1069"
          {...a11yProps(1)}
        />
      </Tabs>

      {isFetchedBrandsData ? (
        <>
          <CustomTabPanel value={value} index={0}>
            <AllBrands />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <NeedsConfiguration />
          </CustomTabPanel>
        </>
      ) : (
        <BrandsSkeleton />
      )}
    </Paper>
  );
};

export default BrandsTabs;
